import styles from "../settings.module.css";
import { useEffect, useState, useMemo } from "react";
import {
  TableBody,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Paper,
  Grid
} from "@mui/material";
import useStyles from "../styles";
import { Icon } from "@iconify/react";
import { httprequest } from "../../../data/api";
import { toast } from "react-toastify";
import PageLoader from "../../../components/PageLoader";
import Button from "../../../components/Button";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ClearIcon from "@mui/icons-material/Clear";
import "rc-slider/assets/index.css";
import DangerModal from "../../../components/DangerModal";
import { useNavigate } from "react-router-dom";
import { logOut, useUserDispatch } from "../../../context/UserContext";

const PRICING_ENDPOINT = "/api/v1/pricing-plans?include_variants=true";
const SUBCRIPTION_ENDPOINT = "/api/v1/subscription";
const CURRENT_ENDPOINT = "api/v1/subscription/current";
const STUDENT_ENDPOINT = "/api/v1/students";

const Subcription = ({ back }) => {
  const classes = useStyles();
  const userDispatch = useUserDispatch();
  const navigate = useNavigate();
  const [pricingModels, setPricingModels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSubcription, setCurrentSubcription] = useState(null);
  const [activeTab, setActiveTab] = useState("pricing");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [studentData, setStudentsData] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Free");
  const [seats, setSeats] = useState(0);
  const [selectedDowngradePlan, setSelectedDowngradePlan] = useState(null);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [understandSeatReduction, setUnderstandSeatReduction] = useState(false);
  const [acceptFeatureLoss, setAcceptFeatureLoss] = useState(false);
  const [understandBilling, setUnderstandBilling] = useState(false);
  const [isDowngrading, setIsDowngrading] = useState(false);
  const [showVariants, setShowVariants] = useState({});
  const [showVariantOptions, setShowVariantOptions] = useState({});
  const [showDowngradeButton, setShowDowngradeButton] = useState(false);
  const [closeAccountModal, setCloseAccountModal] = useState(false);

  // Update URL when tab changes
  const updateURL = (tab) => {
    const url = new URL(window.location);
    const params = new URLSearchParams(url.search);
    params.set("tab", "subscription");

    if (tab === "pricing") {
      params.set("view", "pricing");
    } else if (tab === "summary") {
      params.set("view", "summary");
    } else if (tab === "downgrade") {
      params.set("view", "downgrade");
    }

    window.history.pushState({}, "", `${url.pathname}?${params.toString()}`);
  };

  // Read URL params on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get("view");

    if (viewParam === "pricing") {
      setActiveTab("pricing");
    } else if (viewParam === "summary") {
      setActiveTab("summary");
    } else if (viewParam === "downgrade") {
      setActiveTab("downgrade");
    } else {
      // Default to pricing if no view param
      setActiveTab("pricing");
      updateURL("pricing");
    }
  }, []);

  // Update URL whenever activeTab changes
  useEffect(() => {
    if (activeTab) {
      updateURL(activeTab);
    }
  }, [activeTab]);

  const openDowngradeModal = () => {
    setCloseAccountModal(true);
  };

  const closeDowngradeModal = () => {
    setCloseAccountModal(false);
    setUnderstandSeatReduction(false);
    setAcceptFeatureLoss(false);
    setUnderstandBilling(false);
  };

  const toggleVariants = (planId) => {
    setShowVariants(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const toggleVariantOptions = (planId) => {
    setShowVariantOptions(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const getCurrentPlan = useMemo(() => {
    if (!currentSubcription || !pricingModels?.length) return null;

    const currentPlanId = currentSubcription?.plan_id;
    const currentPlan = pricingModels?.find(plan => plan?.id === currentPlanId);

    if (!currentPlan) return null;

    if (currentPlan?.has_variant && currentSubcription?.plan_variant_id) {
      const currentVariant = currentPlan?.variants?.find(
        variant => variant?.id === currentSubcription?.plan_variant_id
      );

      return {
        ...currentPlan,
        current_variant: currentVariant,
        current_max_users: currentVariant?.max_users || currentPlan?.max_users
      };
    }

    return {
      ...currentPlan,
      current_max_users: currentPlan?.max_users
    };
  }, [currentSubcription, pricingModels]);

  const PriceCardItem = ({
    plan,
    isCurrentPlan,
    onSelectPlan,
    features,
    isRecommended = false,
    showVariantOptions = false,
    onToggleVariantOptions
  }) => {
    const classes = useStyles();

    const getPriceDisplay = () => {
      if (plan?.has_variant && plan.variants?.length) {
        const minPrice = Math.min(...plan.variants.map(v => v?.price));

        return `From ${plan?.currency || "N"}${minPrice?.toLocaleString()}`;
      }

      return `${plan?.currency || "N"}${plan?.price?.toLocaleString()}`;
    };

    const handleCardClick = () => {
      if (plan?.has_variant && plan?.variants?.length > 0) {
        onToggleVariantOptions(plan?.id);
      } else {
        onSelectPlan(plan?.id);
      }
    };

    return (
      <div className={`${classes.netflixCard} ${isCurrentPlan ? "current" : ""} ${isRecommended ? "recommended" : ""}`}>
        <div className={classes.cardHeader}>
          <h3>{plan?.name}</h3>
          <div className={classes.price}><h3>{getPriceDisplay()} /Month</h3></div>
          {isCurrentPlan && (
            <div className={classes.currentPlanBadgeTable}>CURRENT PLAN</div>
          )}
        </div>

        <div className={classes.cardBody}>
          <ul>
            {features?.slice(0, 6).map((feature, index) => (
              <li key={index}>
                {feature === "NULL" ? (
                  <ClearIcon className={classes.cross} />
                ) : (
                  <CheckCircleIcon className={classes.checkmark} />
                )}
                {feature === "NULL" ? "Not available" : feature}
              </li>
            ))}
          </ul>

          {showVariantOptions && plan.has_variant && plan.variants?.length > 0 && (
            <div className={classes.variantOptions}>
              <h4>Select Variant:</h4>
              {plan.variants.map((variant, index) => (
                <div
                  key={index}
                  className={classes.variantOption}
                  onClick={() => onSelectPlan(plan.id, { ...plan, current_variant: variant })}
                >
                  <div className={classes.variantInfo}>
                    <strong>{variant?.name}{getCurrentPlan?.current_variant?.name === variant?.name && " (C)"}</strong>
                    <span>{variant?.max_users} users</span>
                  </div>
                  <div className={classes.variantPrice}>
                    {plan.currency}{variant.price.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={classes.cardFooter}>
          <div
            className={isCurrentPlan ? "secondary" : "primary"}
            onClick={handleCardClick}
          >
            {plan.has_variant && plan.variants?.length > 0 ? "View Plans" :
              isCurrentPlan ? "Current Plan" : "View Plan"}
          </div>
        </div>
      </div>
    );
  };

  const getCurrentCategory = useMemo(() => {
    return getCurrentPlan?.name || "Free";
  }, [getCurrentPlan]);

  const isSelectedPlanLower = useMemo(() => {
    if (!selectedPlan || !getCurrentPlan) return false;

    const currentPrice = getCurrentPlan?.current_variant?.price || getCurrentPlan?.price;
    const selectedPrice = selectedPlan?.current_variant?.price || selectedPlan?.price;

    return selectedPrice < currentPrice;
  }, [selectedPlan, getCurrentPlan]);

  useEffect(() => {
    if (selectedPlan && getCurrentPlan) {
      const isLower = isSelectedPlanLower;
      setShowDowngradeButton(isLower);
    }
  }, [selectedPlan, getCurrentPlan, isSelectedPlanLower]);

  useEffect(() => {
    if (getCurrentPlan && pricingModels.length > 0) {
      setSelectedPlan(getCurrentPlan);
      setActiveCategory(getCurrentCategory);

      if (studentData?.length > 0) {
        setSeats(studentData?.length);
      }
    }
  }, [getCurrentPlan, pricingModels, studentData, getCurrentCategory]);

  const handleBack = () => {
    if (activeTab === "downgrade") {
      setActiveTab("summary");
    } else if (activeTab === "summary") {
      setActiveTab("pricing");
    } else if (activeTab === "pricing") {
      back();
    }
  };

  const handleSelectPlan = (modelId, planWithVariant = null) => {
    const chosen = planWithVariant || pricingModels.find(m => m.id === modelId);

    if (!chosen || !getCurrentPlan) return;

    const currentPrice = getCurrentPlan?.current_variant?.price || getCurrentPlan?.price;
    const chosenPrice = chosen?.current_variant?.price || chosen?.price;

    setShowVariantOptions({});

    if (chosenPrice < currentPrice) {
      setSelectedDowngradePlan(chosen);
      openDowngradeModal();
    } else {
      setActiveTab("summary");
      setSelectedPlan(chosen);
      setActiveCategory(chosen?.name);
    }
  };

  const getAllFeatures = useMemo(() => {
    const featureSet = new Set();
    pricingModels?.forEach(plan => {
      if (plan.allowed_features) {
        plan.allowed_features?.forEach(feature => {
          if (feature !== "NULL") {
            featureSet.add(feature);
          }
        });
      }
    });

    return Array.from(featureSet);
  }, [pricingModels]);

  const getActivePlan = () => {
    const freePlan = pricingModels?.find((p) => p?.name === "Free");
    const proPlan = pricingModels?.find((p) => p?.name === "Pro");
    const enterprisePlan = pricingModels?.find((p) => p?.name === "Enterprise");

    if (freePlan && seats <= freePlan?.max_users) {
      return freePlan;
    }

    if (proPlan && proPlan?.variants?.length) {
      const chosenVariant = proPlan?.variants.find(
        (variant) => seats <= variant?.max_users
      );

      if (chosenVariant) {
        return {
          ...proPlan,
          current_variant: chosenVariant,
          current_max_users: chosenVariant?.max_users
        };
      }
    }

    if (enterprisePlan && seats <= enterprisePlan?.max_users) {
      return enterprisePlan;
    }

    return enterprisePlan || proPlan || freePlan;
  };

  const activePlan = getActivePlan();

  const getDowngradePlans = useMemo(() => {
    if (!getCurrentPlan || !pricingModels?.length) return [];

    const currentPlanPrice = getCurrentPlan?.current_variant?.price || getCurrentPlan?.price;
    const currentPlanMaxUsers = getCurrentPlan?.current_max_users;

    return pricingModels?.filter(plan => {
      if (plan.name === "Free") return true;

      if (plan?.has_variant && plan?.variants?.length) {
        return plan?.variants?.some(variant => variant?.price < currentPlanPrice || variant?.max_users < currentPlanMaxUsers
        );
      }

      return plan?.price < currentPlanPrice || plan?.max_users < currentPlanMaxUsers;
    });
  }, [getCurrentPlan, pricingModels]);

  const fetchPricingModels = () => {
    setIsLoading(true);
    httprequest.get(PRICING_ENDPOINT)
      .then(res => {
        const sortedModels = res?.data?.data.sort((a, b) => a.price - b.price);
        setPricingModels(sortedModels);
      })
      .catch(err => {
        toast.error(err?.response?.data?.message);
      }).finally(() => {
        setIsLoading(false);
      });
  };

  const getAllStudents = () => {
    httprequest.get(STUDENT_ENDPOINT).then((res) => {
      setStudentsData(res?.data?.data);
      setSeats(res?.data?.data?.length);
    }).catch((err) => {
      toast.error(err?.response?.data?.message);
    });
  };

  const getRemainingDays = () => {
    const today = new Date();
    const todayDate = today?.getDate();

    if (todayDate === 1) {
      return 0;
    }

    if (todayDate >= 28) {
      return 0;
    }

    const remainingDays = 30 - todayDate;

    return Math.max(0, Math.min(30, remainingDays));
  };

  const fetchCurrentSubcription = () => {
    httprequest.get(CURRENT_ENDPOINT)
      .then(res => {
        setCurrentSubcription(res?.data?.data);
      })
      .catch(err => {
        setCurrentSubcription(null);
        toast.error(err.response?.data?.message);
      });
  };

  const handleUpgrade = () => {
    if (!selectedPlan || isUpgrading) return;

    setIsUpgrading(true);

    const payload = {
      plan_id: selectedPlan.id,
      plan_variant_id: selectedPlan?.current_variant?.id,
      callback_url: `${process.env.REACT_APP_BASE_URL}/auth/login`
    };

    httprequest.post(SUBCRIPTION_ENDPOINT, payload)
      .then(response => {
        toast.success(response.data.message);
        if (response.data.data.redirect_url) {
          window.location.href = response?.data?.data?.redirect_url;
        }
      })
      .catch(error => {
        toast.error(error.response?.data?.message || "Upgrade failed");
      })
      .finally(() => {
        setIsUpgrading(false);
      });
  };

  const handleDowngrade = (plan) => {
    if (!plan) return;
    setIsDowngrading(true);

    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;

    const payload = {
      plan_id: plan.id,
      plan_variant_id: plan?.current_variant?.id,
      callback_url: `${baseUrl}/auth/login`
    };

    httprequest.post(SUBCRIPTION_ENDPOINT, payload)
      .then(response => {
        toast.success(response?.data?.message);
        setTimeout(() => {
          logOut(userDispatch, navigate);
        }, 1000);
        setIsDowngrading(false);
      })
      .catch(error => {
        toast.error(error?.response?.data?.message || "Downgrade failed");
        setIsDowngrading(false);
      });
  };

  const handleSelectDowngradePlan = (plan) => {
    let selectedPlanWithVariant = { ...plan };
    if (plan?.has_variant && plan?.variants?.length) {
      selectedPlanWithVariant = {
        ...plan,
        current_variant: plan?.variants[0]
      };
    }
    setSelectedDowngradePlan(selectedPlanWithVariant);
  };

  const handleSelectDowngradeVariant = (variant) => {
    if (selectedDowngradePlan) {
      const updatedPlan = {
        ...selectedDowngradePlan,
        current_variant: variant
      };
      setSelectedDowngradePlan(updatedPlan);
    }
  };

  const calculatePricing = () => {
    if (!selectedPlan || !getCurrentPlan) return null;

    const parsePrice = (value) => {
      if (!value && value !== 0) return 0;
      if (typeof value === "number") return value;

      return Number(value.toString().replace(/[^\d.-]/g, "")) || 0;
    };

    const currentPrice = parsePrice(getCurrentPlan?.current_variant?.price || getCurrentPlan?.price);
    const newPrice = parsePrice(selectedPlan?.current_variant?.price || selectedPlan?.price);

    const remainingDays = getRemainingDays();
    const daysUsed = 30 - remainingDays;

    const currentDaily = currentPrice / 30;
    const newDaily = newPrice / 30;

    let amountDue;
    let amountUsed;

    if (remainingDays === 0 || remainingDays === 30) {
      amountDue = newPrice;
      amountUsed = currentPrice;
    } else if (currentPrice === 0) {
      amountDue = newDaily * remainingDays;
      amountUsed = 0;
    } else {
      amountDue = Math.max(0, (newDaily * remainingDays) - (currentDaily * remainingDays));
      amountUsed = currentDaily * daysUsed;
    }

    const newPlanRemainingValue = newDaily * remainingDays;
    const currentPlanUnusedValue = currentDaily * remainingDays;

    return {
      newTierPrice: newPrice,
      currentTierPricing: currentPrice,
      proRataPricing: newDaily,
      amountUsed,
      amountDue,
      nextRenewal: newPrice,
      remainingDays,
      daysUsed,
      isUpgrade: newPrice > currentPrice,
      isDowngrade: newPrice < currentPrice,
      currentDaily,
      newDaily,
      newPlanRemainingValue,
      currentPlanUnusedValue,
      amountDueDisplay: amountDue
    };
  };

  const pricingSummary = calculatePricing();

  const handleCategoryChange = (categoryName) => {
    setActiveCategory(categoryName);
    const categoryPlan = pricingModels?.find(p => p?.name === categoryName);

    if (categoryPlan) {
      if (categoryPlan?.has_variant && categoryPlan?.variants?.length) {
        setSelectedPlan({
          ...categoryPlan,
          current_variant: categoryPlan?.variants[0]
        });
      } else {
        setSelectedPlan(categoryPlan);
      }
    }
  };

  const handleVariantSelect = (variant) => {
    if (selectedPlan && selectedPlan?.has_variant) {
      const updatedPlan = {
        ...selectedPlan,
        current_variant: variant
      };
      setSelectedPlan(updatedPlan);
    }
  };

  const isCurrentPlan = (model) => {
    if (!getCurrentPlan) return false;

    return getCurrentPlan?.id === model?.id;
  };

  const reloadAllData = () => {
    setIsLoading(true);
    Promise.all([
      getAllStudents(),
      fetchPricingModels(),
      fetchCurrentSubcription()
    ]).finally(() => {
      setIsLoading(false);
    });
  };

  const arePlansDifferent = useMemo(() => {
    if (!getCurrentPlan || !selectedPlan) return false;

    if (getCurrentPlan?.id !== selectedPlan?.id) return true;

    const currentVariantId = getCurrentPlan?.current_variant?.id;
    const selectedVariantId = selectedPlan?.current_variant?.id;

    return currentVariantId !== selectedVariantId;
  }, [getCurrentPlan, selectedPlan]);

  useEffect(() => {
    reloadAllData();
  }, []);

  const currency = selectedPlan?.currency || "₦";
  const selectedPlanName = selectedPlan?.name || "Free";

  const fmt = (v) => {
    if (v == null || isNaN(v)) return "0.00";

    return Number(v).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  useEffect(() => {
    if (activeTab === "downgrade" && getDowngradePlans?.length > 0 && !selectedDowngradePlan) {
      const firstPlan = getDowngradePlans[0];

      if (firstPlan.has_variant && firstPlan?.variants?.length) {
        const availableVariant = firstPlan?.variants.find(variant => {
          const currentPrice = getCurrentPlan?.current_variant?.price || getCurrentPlan?.price;
          const currentMaxUsers = getCurrentPlan?.current_max_users;

          return variant?.price < currentPrice || variant?.max_users < currentMaxUsers;
        });

        if (availableVariant) {
          setSelectedDowngradePlan({
            ...firstPlan,
            current_variant: availableVariant
          });
        }
      } else {
        setSelectedDowngradePlan(firstPlan);
      }
    }
  }, [activeTab, getDowngradePlans, selectedDowngradePlan, getCurrentPlan]);

  // const freeFeatures = [
  //   "Up to 99 users", "Comprehensive Dashboard", "Payments Management", "Reports Management", "Up to 50 Applications/Enrolment", "NULL", "NULL", "NULL"
  // ];
  const renderFeatureComparisonTable = () => {
    if (pricingModels.length === 0) return null;

    const anyVariantsShown = Object.values(showVariants).some(v => v);

    return (
      <div className={classes.pricingTableContainer}>
        <h2 style={{ textAlign: "center", marginBottom: "32px", color: "#333" }}>
          Compare Plans
        </h2>

        <TableContainer>
          <Table className={classes.pricingTable}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.featureName}>Features</TableCell>
                {pricingModels.map((plan) => (
                  <TableCell key={plan.id} className={classes.planHeader}>
                    <h3>{plan.name}</h3>
                    <div className={classes.price}>
                      {plan.has_variant && plan.variants?.length
                        ? `From ${plan.currency || "N"}${Math.min(...plan.variants.map(v => v.price)).toLocaleString()} /Month`
                        : `${plan.currency || "N"}${plan.price.toLocaleString()} /Month`
                      }
                    </div>
                    {plan.has_variant && plan.variants?.length > 0 && (
                      <div
                        onClick={() => toggleVariants(plan.id)}
                        className={classes.toggleVariant}
                      >
                        {showVariants[plan.id] ? "Hide Variants" : "Show Variants"}
                      </div>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!anyVariantsShown && getAllFeatures.map((feature, featureIndex) => (
                <TableRow key={featureIndex} className={classes.featureRow}>
                  <TableCell className={classes.featureName}>
                    {feature.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                  </TableCell>
                  {pricingModels.map((plan) => {
                    const hasFeature = plan?.allowed_features?.includes(feature);

                    return (
                      <TableCell key={plan.id} align="center">
                        {hasFeature ? (
                          <CheckCircleIcon className={classes.checkmark} />
                        ) : (
                          <ClearIcon className={classes.cross} />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}

              {anyVariantsShown && pricingModels.map((plan) => (
                showVariants[plan.id] && plan.has_variant && plan.variants?.length > 0 && (
                  <>
                    <TableRow key={`variant-header-${plan.id}`}>
                      <TableCell colSpan={pricingModels.length + 1} className={classes.toggledVariantDeets}>
                        {plan.name} Plan Variants
                      </TableCell>
                    </TableRow>

                    {plan.variants.map((variant) => (
                      <TableRow key={variant.id}>
                        <TableCell className={classes.featureName}>
                          {variant.name} ({variant.max_users} users)
                        </TableCell>
                        {pricingModels.map((p) => (
                          <TableCell key={p.id} className={classes.backgroundOnly}>
                            {p.id === plan.id ? (
                              <div className={classes.selectagain}>
                                <h4>
                                  {plan.currency}{variant.price.toLocaleString()}
                                </h4>
                                <div className={classes.selectTable}>
                                  <Button
                                    variant="primary"
                                    buttonSize="small"
                                    color="btndefault"
                                    buttonName="Select"
                                    onClick={() => {
                                      const planWithVariant = {
                                        ...plan,
                                        current_variant: variant
                                      };
                                      handleSelectPlan(plan.id, planWithVariant);
                                    }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <div>-</div>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </>
                )
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  };

  const renderPriceCardItem = () => {
    const cardHeights = pricingModels.map(plan => {
      const baseHeight = 400;
      const variantHeight = plan.has_variant && showVariantOptions[plan.id] ? plan.variants.length * 60 : 0;

      return baseHeight + variantHeight;
    });

    const maxCardHeight = Math.max(...cardHeights);

    return (
      <Grid container spacing={2} style={{ marginBottom: "32px" }}>
        {pricingModels.map((plan, index) => (
          <Grid item lg={4} md={6} sm={12} xs={12} key={plan.id}>
            <div style={{ height: `${maxCardHeight}px` }}>
              <PriceCardItem
                plan={plan}
                isCurrentPlan={isCurrentPlan(plan)}
                onSelectPlan={handleSelectPlan}
                onToggleVariantOptions={toggleVariantOptions}
                showVariantOptions={showVariantOptions[plan.id]}
                features={plan.features}
                isRecommended={index === 1 && plan?.name === "Pro" && getCurrentPlan?.name !== "Enterprise"}
              />
            </div>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <div className={styles.pagecontent}>
      <button onClick={handleBack} className={classes.goBackButton}>
        <Icon icon="material-symbols:arrow-back" width="24" height="24" />
        Go Back
      </button>

      {isLoading ? (
        <div className="page-content margin-top-30" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100px" }}>
          <PageLoader />
        </div>
      ) : (
        <>
          {activeTab === "pricing" && (
            <>
              <div style={{ textAlign: "center", marginBottom: "40px" }}>
                <h1 style={{ fontSize: "2.5rem", fontWeight: "700", color: "#333", marginBottom: "4px" }}>
                  Choose Your Plan
                </h1>
                <p style={{ fontSize: "1.2rem", color: "#666", maxWidth: "600px", margin: "0 auto" }}>
                  Select the perfect plan for your school. All plans include our core features with scalable options.
                </p>
              </div>

              {renderPriceCardItem()}
              {renderFeatureComparisonTable()}
            </>
          )}

          {activeTab === "summary" && (
            <div className={classes.container}>
              <div className="flex justify-content-between align-items-center margin-bottom-14">
                <h2>
                  {isSelectedPlanLower ? "Downgrade Plan" : "Change Plan"}
                </h2>
              </div>

              {currentSubcription?.message ? (
                <div className={classes.alert}>
                  You have unpaid invoices. Cost-increasing actions (add users, enable modules) are blocked until
                  payment is made. Due: Sep 30, 2025 (14 day(s) remaining)
                </div>
              ) : null}

              <Grid container spacing={1} className="input-padding">
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <div className={classes.currentSeat}>
                    <p className={classes.currentSeatTitle}>Your Current Seat</p>
                    <h1 className={classes.seatCount}>
                      <span className={classes.seatLabel}>{studentData?.length} Seats</span>
                    </h1>
                  </div>

                  <div className={classes.plansSection}>
                    <div className={classes.wrapper}>
                      <div className={classes.categories}>
                        {[...pricingModels]
                          ?.sort((a, b) => a.price - b.price)
                          ?.filter(cat => {
                            if (!getCurrentPlan) return true;

                            const currentPrice = getCurrentPlan?.current_variant?.price || getCurrentPlan?.price;
                            const categoryPrice = cat?.price;

                            const isHigherCategory = categoryPrice > currentPrice;
                            const hasVariants = cat?.has_variant && cat?.variants?.length > 0;
                            const isCurrentCategory = cat?.name === getCurrentPlan?.name;

                            return isHigherCategory || (hasVariants && isCurrentCategory) || hasVariants;
                          })
                          ?.map((cat, index) => (
                            <div
                              key={index}
                              className={`${classes.categoryBtn} ${
                                activeCategory === cat?.name ? classes.activeCategory : ""
                              } ${getCurrentPlan?.name === cat?.name ? classes.currentCategory : ""}`}
                              onClick={() => handleCategoryChange(cat?.name)}
                            >
                              {cat?.name}
                              {getCurrentPlan?.name === cat?.name && " (C)"}
                            </div>
                          ))}
                      </div>

                      <div className={classes.plans}>
                        {pricingModels
                          ?.filter((plan) => plan?.name === activeCategory)
                          ?.map((plan) => {
                            if (plan?.has_variant && Array.isArray(plan.variants)) {
                              return plan?.variants
                                .filter(variant => {
                                  if (!getCurrentPlan?.current_variant) return true;

                                  const currentVariantPrice = getCurrentPlan?.current_variant?.price;
                                  const currentVariantMaxUsers = getCurrentPlan?.current_variant?.max_users;

                                  return variant.price > currentVariantPrice ||
                                    variant.max_users > currentVariantMaxUsers ||
                                    variant.id === getCurrentPlan.current_variant.id;
                                })
                                .map((variant, idx, filteredVariants) => {
                                  const prevMax = idx > 0 ? filteredVariants[idx - 1].max_users : 0;
                                  const minRange = prevMax + 1;
                                  const maxRange = variant?.max_users;

                                  const isHighlighted = seats >= minRange && seats <= maxRange;
                                  const isCurrentVariant = getCurrentPlan?.current_variant?.id === variant.id;
                                  const isSelectedPlan = selectedPlan?.current_variant?.id === variant.id;

                                  const handleSelectVariant = () => {
                                    setSeats(maxRange);
                                    handleVariantSelect(variant);
                                  };

                                  return (
                                    <div
                                      key={variant.id}
                                      onClick={handleSelectVariant}
                                      className={`${classes.planCard} ${
                                        isHighlighted ? classes.highlightCard : ""
                                      } ${isCurrentVariant ? classes.currentPlanCard : ""} ${isSelectedPlan ? classes.selectedCard : ""}`}
                                    >
                                      <h4>{variant?.name}</h4>
                                      <p>
                                        {idx === 0
                                          ? `Up to ${variant?.max_users}`
                                          : `${minRange} - ${variant?.max_users}`} Students
                                      </p>
                                      <p>
                                        <strong>
                                          ₦{Number(variant?.price || 0)?.toLocaleString("en-US")}
                                        </strong>
                                      </p>
                                    </div>
                                  );
                                });
                            }

                            if (!getCurrentPlan) {
                              const isHighlighted = seats >= 0 && seats <= (plan?.max_users || Infinity);
                              const isCurrentPlanCard = getCurrentPlan?.id === plan?.id;

                              const handleSelectPlan = () => {
                                setSeats(plan.max_users || 0);
                              };

                              return (
                                <div
                                  key={plan.id}
                                  onClick={handleSelectPlan}
                                  className={`${classes.planCard} ${
                                    isHighlighted ? classes.highlightCard : ""
                                  } ${isCurrentPlanCard ? classes.currentPlanCard : ""}`}
                                >
                                  <h4>{plan?.name}</h4>
                                  <p>Up to {plan?.max_users} Students</p>
                                  <p>
                                    <strong>
                                      ₦{Number(plan?.price || 0).toLocaleString("en-US")}
                                    </strong>
                                  </p>
                                </div>
                              );
                            }

                            const currentPrice = getCurrentPlan?.current_variant?.price || getCurrentPlan?.price;
                            const planPrice = plan?.price;

                            if (planPrice > currentPrice) {
                              const isHighlighted = seats >= 0 && seats <= (plan?.max_users || Infinity);
                              const isCurrentPlanCard = getCurrentPlan?.id === plan?.id;

                              const handleSelectPlan = () => {
                                setSeats(plan.max_users || 0);
                              };

                              return (
                                <div
                                  key={plan.id}
                                  onClick={handleSelectPlan}
                                  className={`${classes.planCard} ${
                                    isHighlighted ? classes.highlightCard : ""
                                  } ${isCurrentPlanCard ? classes.currentPlanCard : ""}`}
                                >
                                  <h4>{plan.name} {isCurrentPlanCard && "(Current)"}</h4>
                                  <p>Up to {plan.max_users} Students</p>
                                  <p>
                                    <strong>
                                      ₦{Number(plan?.price || 0).toLocaleString("en-US")}
                                    </strong>
                                  </p>
                                </div>
                              );
                            }

                            return null;
                          })}
                      </div>
                    </div>

                    {activePlan && (
                      <div className={`${classes.note} flex align-items-center gap-10`}>
                        <Icon icon="ep:warning" />
                        <p>
                          Max Seat capacity for {activePlan?.name} is {activePlan?.max_users} Students
                        </p>
                      </div>
                    )}
                  </div>
                </Grid>

                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <div className={classes.leftItem}>
                    <div className={classes.leftheader}>
                      <p className={classes.subTitle}>Subscription Summary</p>
                    </div>
                    <div className={classes.pricingBox}>
                      <div className={classes.pricesinfo}>
                        <p>Current Plan</p>
                        <h3>
                          {getCurrentPlan?.current_variant ? ` ${getCurrentPlan?.current_variant?.name}` : getCurrentPlan?.name}
                        </h3>
                      </div>
                      <div className={classes.pricesinfo}>
                        <p>Selected Tier</p>
                        <h3>
                          {selectedPlan?.current_variant ? ` ${selectedPlan?.current_variant?.name}` : selectedPlanName}
                        </h3>
                      </div>
                      <div className={classes.pricesinfo}>
                        <p>New Tier Price</p>
                        <h3>
                          {currency} {Number(pricingSummary?.newTierPrice || 0)?.toLocaleString()}
                        </h3>
                      </div>
                      <div className={classes.pricesinfo}>
                        <p>Remaining Days</p>
                        <h3>{pricingSummary?.remainingDays ?? "--"} days</h3>
                      </div>
                      <div className={classes.pricesinfo}>
                        <p>Pro Rata Pricing</p>
                        <h3>
                          {currency} {Number(pricingSummary?.proRataPricing?.toFixed(0) || 0).toLocaleString()}
                        </h3>
                      </div>
                      <div className={classes.pricesinfo}>
                        <p>Current Tier Pricing</p>
                        <h3>
                          {currency} {Number(pricingSummary?.currentTierPricing || 0).toLocaleString()}
                        </h3>
                      </div>
                      <div className={classes.pricesinfo}>
                        <p>Amount Used</p>
                        <h3>
                          {currency} {Number(pricingSummary?.amountUsed?.toFixed(0) || 0).toLocaleString()}
                        </h3>
                      </div>
                      <div className={classes.pricesinfo}>
                        <h4>Amount due now</h4>
                        <h4>
                          {currency} {Number(pricingSummary?.amountDue?.toFixed(0) || 0).toLocaleString()}
                        </h4>
                      </div>
                      <div className={classes.pricesinfo}>
                        <h4>Amount on next renewal</h4>
                        <h4>
                          {currency} {Number(pricingSummary?.nextRenewal?.toFixed(0) || 0).toLocaleString()}
                        </h4>
                      </div>
                    </div>
                  </div>

                  <div className={classes.chargesBox}>
                    <p>
                      <strong>How your charges are calculated:</strong>
                    </p>

                    {pricingSummary ? (
                      <ul>
                        <li>
                          Pro rata price of your <strong>current</strong> plan —{" "}
                          {currency} {fmt(pricingSummary.currentDaily?.toFixed(0))}/day
                        </li>

                        <li>
                          Pro rata price of your <strong>new</strong> plan —{" "}
                          {currency} {fmt(pricingSummary.newDaily?.toFixed(0))}/day
                        </li>

                        <li>
                          Amount used — {currency} {fmt(pricingSummary.currentDaily?.toFixed(0))} × {pricingSummary.daysUsed} day{pricingSummary.daysUsed !== 1 ? "s" : ""} ={" "}
                          {currency} {fmt(pricingSummary.amountUsed?.toFixed(0))}
                        </li>

                        <li>
                          Amount due calculation — ({currency} {fmt((pricingSummary.newDaily * pricingSummary.remainingDays)?.toFixed(0))} (new plan for{" "}
                          {pricingSummary.remainingDays} day{pricingSummary.remainingDays !== 1 ? "s" : ""}) − {currency}{" "}
                          {fmt((pricingSummary.currentDaily * pricingSummary.remainingDays)?.toFixed(0))} (unused value of current plan)) ={" "}
                          <strong>{currency} {fmt(pricingSummary.amountDueDisplay?.toFixed(0))}</strong>
                        </li>
                      </ul>
                    ) : (
                      <p className={classes.muted}>Select a plan to see the charge breakdown.</p>
                    )}
                  </div>
                </Grid>
              </Grid>

              <div className={classes.modalFooterBtn}>
                {showDowngradeButton && (
                  <Button
                    variant="danger"
                    buttonSize="fluid"
                    color="btndefault"
                    buttonName="Proceed to Downgrade"
                    onClick={() => setActiveTab("downgrade")}
                  />
                )}
                {arePlansDifferent && (
                  <Button
                    variant="primary"
                    buttonSize="fluid"
                    color="btndefault"
                    buttonName="Confirm Upgrade"
                    isLoading={isUpgrading}
                    onClick={handleUpgrade}
                  />
                )}
              </div>
            </div>
          )}

          {activeTab === "downgrade" && (
            <div className={classes.downgradeContainer}>
              <div className="flex justify-content-between align-items-center">
                <h2>Downgrade Plan</h2>
                <Button
                  variant="primary"
                  buttonSize="medium"
                  color="btndefault"
                  buttonName="Back to Summary"
                  onClick={() => setActiveTab("summary")}
                />
              </div>

              <p className={classes.subtitle}>
                You are moving from {getCurrentPlan?.name} Plan to a lower tier plan.
              </p>

              <div className={classes.plansContainer}>
                <Grid container spacing={2}>
                  {getDowngradePlans.slice().sort((a, b) => b.max_users - a.max_users).map((plan, index) => (
                    <Grid item lg={4} md={6} sm={6} xs={12} key={index}>
                      <div
                        className={`${classes.aplanCard} ${
                          plan.name === "Free" ? classes.freePlanCard :
                            plan.name === "Pro" ? classes.proPlanCard :
                              classes.enterprisePlanCard
                        } ${selectedDowngradePlan?.id === plan.id ? classes.selectedDowngradeCard : ""}`}
                        onClick={() => handleSelectDowngradePlan(plan)}
                      >
                        <h3 className={classes.planTitle}>{plan.name} Plan</h3>
                        <div className={classes.planSeats}>
                          {plan.has_variant && plan.variants?.length
                            ? `${plan?.variants[0].max_users}+ seats`
                            : `${plan.max_users}+ seats`
                          }
                        </div>
                        <div className={classes.planPrice}>
                          {plan.currency || "₦"}{plan.price.toLocaleString()}
                        </div>
                        <div className={classes.planFeatures}>
                          {plan.allowed_features?.slice(0, 3).map((feature, idx) => (
                            <span key={idx} className={classes.featureChip}>
                              {feature.replace(/_/g, " ")}
                            </span>
                          ))}
                          {plan.allowed_features?.length > 3 && (
                            <span className={classes.featureChip}>+{plan.allowed_features.length - 3} more</span>
                          )}
                        </div>
                      </div>
                    </Grid>
                  ))}
                </Grid>
              </div>

              {selectedDowngradePlan?.has_variant && selectedDowngradePlan.variants?.length > 0 && (
                <div className={classes.variantSection}>
                  <h3>Select Variant for {selectedDowngradePlan.name} Plan</h3>
                  <div className={classes.variantsContainer}>
                    <Grid container spacing={2}>
                      {selectedDowngradePlan.variants
                        .filter(variant => {
                          const isCurrentVariant = variant.id === currentSubcription?.pricing_plan_variant?.id;
                          const isHigherThanCurrent = variant.price > (getCurrentPlan?.current_variant?.price || getCurrentPlan?.price);

                          return !isCurrentVariant && !isHigherThanCurrent;
                        })
                        .map((variant, idx, filteredVariants) => {
                          const prevMax = idx > 0 ? filteredVariants[idx - 1].max_users : 0;
                          const minRange = prevMax + 1;

                          return (
                            <Grid item lg={3} md={6} sm={6} xs={12} key={variant.id}>
                              <div
                                className={`${classes.variantCard} ${
                                  selectedDowngradePlan.current_variant?.id === variant.id ? classes.selectedVariantCard : ""
                                }`}
                                onClick={() => handleSelectDowngradeVariant(variant)}
                              >
                                <h4>{variant.name}</h4>
                                <p>
                                  {idx === 0
                                    ? `Up to ${variant.max_users}`
                                    : `${minRange} - ${variant.max_users}`} Students
                                </p>
                                <p className={classes.variantPrice}>
                                  <strong>
                                    {selectedDowngradePlan.currency || "₦"}{Number(variant.price || 0).toLocaleString("en-US")}
                                  </strong>
                                </p>
                                {selectedDowngradePlan.current_variant?.id === variant.id && (
                                  <div className={classes.selectedBadge}>Selected</div>
                                )}
                              </div>
                            </Grid>
                          );
                        })}
                    </Grid>
                  </div>
                </div>
              )}

              {selectedDowngradePlan && (
                <div className={classes.comparisonSection}>
                  <h3>Plan Comparison</h3>
                  <TableContainer component={Paper} className={classes.comparisonTable}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Feature</strong></TableCell>
                          <TableCell align="center"><strong>Current ({getCurrentPlan?.name})</strong></TableCell>
                          <TableCell align="center"><strong>New ({selectedDowngradePlan.name})</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Max Seats</TableCell>
                          <TableCell align="center">{getCurrentPlan?.current_max_users}</TableCell>
                          <TableCell align="center">
                            {selectedDowngradePlan.current_variant?.max_users || selectedDowngradePlan.max_users}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Price</TableCell>
                          <TableCell align="center">
                            {getCurrentPlan?.currency || "₦"}{getCurrentPlan?.current_variant?.price.toLocaleString() || getCurrentPlan?.price.toLocaleString()}
                          </TableCell>
                          <TableCell align="center">
                            {selectedDowngradePlan.currency || "₦"}{selectedDowngradePlan.current_variant?.price.toLocaleString() || selectedDowngradePlan.price.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              )}

              <div className={classes.alert}>
                This downgrade reduces seats to {selectedDowngradePlan?.current_variant?.max_users || selectedDowngradePlan?.max_users}.
                You currently have {studentData?.length} active seats.
                Please manage your Seats to avoid deactivations.
              </div>

              <div className="flex gap-10 justify-content-end">
                <Button
                  variant="primaryoutline"
                  buttonName="Cancel"
                  buttonSize="medium"
                  color="btnfontprimary"
                  onClick={() => setActiveTab("summary")}
                />
                <Button
                  variant="primary"
                  buttonName="Downgrade"
                  buttonSize="medium"
                  color="btndefault"
                  onClick={openDowngradeModal}
                  disabled={!selectedDowngradePlan}
                />
              </div>
            </div>
          )}

          <DangerModal
            title="Downgrade Account"
            subtitle="Important information about your downgrade"
            modalContent={
              <>
                <div className={classes.downgradeWarning}>
                  <Icon icon="ep:warning" width="24" height="24" />
                  <h4>Before You Downgrade</h4>
                </div>

                <Grid container spacing={3} className="input-padding">
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <div className={classes.planComparison}>
                      <h4>Plan Changes</h4>
                      <div className={classes.comparisonGrid}>
                        <div className={classes.comparisonItem}>
                          <span className={classes.comparisonLabel}>Current Plan:</span>
                          <span className={classes.currentPlan}>{getCurrentPlan?.name} {getCurrentPlan?.current_variant && `- ${getCurrentPlan.current_variant.name}`}</span>
                        </div>
                        <div className={classes.comparisonItem}>
                          <span className={classes.comparisonLabel}>New Plan:</span>
                          <span className={classes.newPlan}>{selectedDowngradePlan?.name} {selectedDowngradePlan?.current_variant && `- ${selectedDowngradePlan.current_variant.name}`}</span>
                        </div>
                        <div className={classes.comparisonItem}>
                          <span className={classes.comparisonLabel}>Seat Reduction:</span>
                          <span className={classes.seatChange}>
                            {getCurrentPlan?.current_max_users} → {selectedDowngradePlan?.current_variant?.max_users || selectedDowngradePlan?.max_users} seats
                          </span>
                        </div>
                        <div className={classes.comparisonItem}>
                          <span className={classes.comparisonLabel}>Price Change:</span>
                          <span className={classes.priceChange}>
                            {getCurrentPlan?.currency || "₦"}{(getCurrentPlan?.current_variant?.price || getCurrentPlan?.price)?.toLocaleString()} → {selectedDowngradePlan?.currency || "₦"}{(selectedDowngradePlan?.current_variant?.price || selectedDowngradePlan?.price)?.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <div className={classes.criticalWarning}>
                      <h4>⚠️ Critical Changes</h4>
                      <ul>
                        <li>
                          <strong>Seat Reduction:</strong> You currently have <strong>{studentData?.length}</strong> active seats.
                          Your new plan supports only <strong>{selectedDowngradePlan?.current_variant?.max_users || selectedDowngradePlan?.max_users}</strong> seats.
                          {studentData?.length > (selectedDowngradePlan?.current_variant?.max_users || selectedDowngradePlan?.max_users) && (
                            <span className={classes.highlightWarning}> You must reduce your seat count before proceeding.</span>
                          )}
                        </li>
                        <li>
                          <strong>Feature Limitations:</strong> Some features may become unavailable or limited after downgrading.
                        </li>
                        <li>
                          <strong>Data Access:</strong> Certain reports and historical data might have restricted access.
                        </li>
                        <li>
                          <strong>Billing:</strong> Changes will take effect at the end of your current billing cycle.
                        </li>
                      </ul>
                    </div>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <div className={classes.confirmationSection}>
                      <h4>Confirmation</h4>
                      <div className={classes.checkboxGroup}>
                        <label className={classes.checkboxLabel}>
                          <input
                            type="checkbox"
                            onChange={(e) => setUnderstandSeatReduction(e.target.checked)}
                          />
                          I understand that I need to reduce my seat count from {studentData?.length} to {" "}
                          {selectedDowngradePlan?.current_variant?.max_users || selectedDowngradePlan?.max_users}{" "}
                          before downgrading
                        </label>
                        <label className={classes.checkboxLabel}>
                          <input
                            type="checkbox"
                            onChange={(e) => setAcceptFeatureLoss(e.target.checked)}
                          />
                          I accept that some features will become unavailable after downgrading
                        </label>
                        <label className={classes.checkboxLabel}>
                          <input
                            type="checkbox"
                            onChange={(e) => setUnderstandBilling(e.target.checked)}
                          />
                          I understand this change will take effect at the end of my billing cycle
                        </label>
                      </div>
                    </div>
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Button
                      variant="primaryoutline"
                      buttonName="Cancel"
                      buttonSize="full"
                      color="btnfontprimary"
                      onClick={closeDowngradeModal}
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Button
                      variant="danger"
                      buttonName="Confirm Downgrade"
                      buttonSize="full"
                      color="btndefault"
                      isLoading={isDowngrading}
                      onClick={() => {
                        closeDowngradeModal();
                        handleDowngrade(selectedDowngradePlan);
                      }}
                      disabled={
                        !understandSeatReduction ||
                        !acceptFeatureLoss ||
                        !understandBilling ||
                        (studentData?.length > (selectedDowngradePlan?.current_variant?.max_users || selectedDowngradePlan?.max_users))
                      }
                    />
                  </Grid>
                </Grid>
              </>
            }
            isopen={closeAccountModal}
            closeModal={closeDowngradeModal}
          />
        </>
      )}
      {/* <ToastContainer /> */}
    </div>
  );
};

export default Subcription;
